import {
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
  MatAutocomplete,
  MatOption,
} from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { Country } from 'src/app/services/countries/countries.service';
import { PartyType } from 'src/types';
import { RepresentativeType } from 'src/types/model/schemas';
import {
  CompanySearchAutocompleteItem,
  CompanySearchService,
} from 'src/app/shared/company-search-service/company-search.service';
import { DebtorControls, EditCaseDialogService } from '../edit-case-dialog.service';
import { LockRepresentativesDialogComponent } from '../lock-representatives-dialog/lock-representatives-dialog.component';
import { UnlockRepresentativesDialogComponent } from '../unlock-representatives-dialog/unlock-representatives-dialog.component';
import { PayeeFormFieldComponent } from '../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeInputDirective } from '../../../shared/payee-input/payee-input.directive';
import { PayeeAutocompleteComponent } from '../../../shared/payee-form-fields/payee-autocomplete/payee-autocomplete.component';
import { BankAccountFormComponent } from '../bank-account-form/bank-account-form.component';
import { AddressFormComponent } from '../address-form/address-form.component';
import { PayeeDatepickerComponent } from '../../../shared/payee-form-fields/payee-datepicker/payee-datepicker.component';
import { PayeeIconButtonComponent } from '../../../shared/payee-buttons/payee-icon-button/payee-icon-button.component';
import { PayeeIconComponent } from '../../../shared/payee-icon/payee-icon.component';
import { RepresentativesFormComponent } from '../representatives-form/representatives-form.component';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-debtor-form',
  templateUrl: './debtor-form.component.html',
  styleUrls: ['./debtor-form.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    PayeeFormFieldComponent,
    PayeeInputDirective,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatOption,
    PayeeAutocompleteComponent,
    BankAccountFormComponent,
    AddressFormComponent,
    PayeeDatepickerComponent,
    PayeeIconButtonComponent,
    PayeeIconComponent,
    RepresentativesFormComponent,
    DatePipe,
  ],
})
export class DebtorFormComponent implements OnInit, OnDestroy {
  private dialog = inject(MatDialog);
  private editCaseDialogService = inject(EditCaseDialogService);
  private companySearchService = inject(CompanySearchService);

  private readonly destroy = new Subject<void>();
  private readonly companySearch$ = new Subject<string>();

  autocompleteOptions: CompanySearchAutocompleteItem[] = [];

  readonly form = input.required<FormGroup<DebtorControls>>();
  readonly index = input.required<number>();
  readonly partyTypes = input.required<PartyType[]>();
  readonly representativeTypes = input.required<RepresentativeType[]>();
  readonly countries = input.required<Country[]>();
  readonly partyTypeOptions = computed<number[]>(() =>
    this.partyTypes().map((pt) => pt.id),
  );

  get value() {
    return this.form().value;
  }

  get company_updated_at() {
    return this.form().controls.company_updated_at;
  }
  get name() {
    return this.form().controls.name;
  }
  get bank_account() {
    return this.form().controls.bank_account;
  }
  get address() {
    return this.form().controls.address;
  }
  get tax_number() {
    return this.form().controls.tax_number;
  }
  get birth_date() {
    return this.form().controls.debtor_data.controls.birth_date;
  }
  get birth_name() {
    return this.form().controls.debtor_data.controls.birth_name;
  }
  get birth_place() {
    return this.form().controls.debtor_data.controls.birth_place;
  }
  get employer_name() {
    return this.form().controls.debtor_data.controls.employer_name;
  }
  get mother_name() {
    return this.form().controls.debtor_data.controls.mother_name;
  }
  get personal_id() {
    return this.form().controls.debtor_data.controls.personal_id;
  }
  get registration_number() {
    return this.form().controls.debtor_data.controls.registration_number;
  }
  get party_type_id() {
    return this.form().controls.party_type_id;
  }
  get representatives() {
    return this.form().controls.representatives;
  }

  ngOnInit(): void {
    this.companySearch$
      .pipe(takeUntil(this.destroy), debounceTime(500))
      .subscribe(async (name) => {
        try {
          this.autocompleteOptions =
            await this.companySearchService.getCompanySearchAutocompleteItems(
              name,
            );
        } catch (error) {
          console.error('Error while searching debtor', error);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
    this.companySearch$.complete();
  }

  onCompanySearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement | null)?.value ?? '';
    this.companySearch$.next(value);
  }

  async companySelected(event: MatAutocompleteSelectedEvent): Promise<void> {
    const item = event.option.value as CompanySearchAutocompleteItem;
    await this.editCaseDialogService.applyCompanySearchToParty(
      this.form(),
      item,
      this.registration_number,
    );
  }

  lockRepresentatives(): void {
    this.dialog.open(LockRepresentativesDialogComponent, {
      data: {
        lock_representatives: this.form().controls.lock_representatives,
        representatives: this.form().controls.representatives,
      },
    });
  }

  unlockRepresentatives(): void {
    this.dialog.open(UnlockRepresentativesDialogComponent, {
      data: {
        lock_representatives: this.form().controls.lock_representatives,
        representatives: this.form().controls.representatives,
      },
    });
  }

  isOrg(debtorForm: FormGroup<DebtorControls>): boolean {
    return (
      this.partyTypes().find((p) => p.id === debtorForm.value.party_type_id)
        ?.type !== 'ind'
    );
  }

  isHouse(debtorForm: FormGroup<DebtorControls>): boolean {
    return (
      this.partyTypes().find((p) => p.id === debtorForm.value.party_type_id)
        ?.internal_id === '91-a'
    );
  }

  readonly partyTypeDisplayWith = (id: number): string => {
    return this.partyTypes().find((pt) => pt.id == id)?.label || '';
  };
}
